import Link from '@/routing/AppLink';
import { useTranslation } from 'next-i18next/pages';

import Area from '../shared/Area';
import {
  FooterCompanyInfo,
  FooterCopyright,
} from '../shared/FooterCompanyInfo';
import { FooterNav } from '../shared/FooterNav';
import FooterVisualBand from '../shared/FooterVisualBand';
import type { FooterColor, FooterTone } from '../shared/footerTheme';

type Props = {
  color?: FooterColor;
};

/**
 * 원본 Emotion 은 부모의 color prop 으로 자식 셀렉터를 덮어썼다.
 * Tailwind 로 옮기면서 자식별 클래스 맵으로 편다.
 * FooterBottom 의 border-top-color 는 border-width 가 없어 실제로 렌더되지 않지만,
 * 동작을 바꾸지 않기 위해 그대로 옮긴다.
 *
 * value 는 PC 푸터와 다른 회색을 쓴다. 옮겨온 원본이 그랬고, 색을 맞추는
 * 판단은 디자인 확인이 필요해 여기서 함께 바꾸지 않는다.
 */
const THEME: Record<FooterColor, FooterTone> = {
  white: {
    container: 'bg-apps-coreGrayWhite',
    label: 'text-apps-coreGray02',
    value: 'text-apps-coreGrayBlack',
    bottom: 'border-t-apps-coreProductDivider',
    terms: 'text-apps-coreGray02',
    termsBold: 'text-apps-coreGrayBlack',
  },
  black: {
    container: 'bg-apps-coreGrayBlack',
    label: 'text-apps-coreGray03',
    value: 'text-apps-coreGrayWhite',
    bottom: 'border-t-apps-coreGray01',
    terms: 'text-apps-coreGray03',
    termsBold: 'text-apps-brandGray950',
  },
};

const TERMS_ITEM = 'text-sm leading-[140%] tracking-[-0.28px]';

export default function Footer({ color = 'black' }: Props) {
  const { t } = useTranslation('common');
  const tone = THEME[color];

  return (
    <div className={`flex flex-col pb-10 ${tone.container}`}>
      <FooterVisualBand variant="mobile" />
      <Area>
        {/* 원본은 padding-bottom: 24px 뒤에 padding: 40px 0 가 와서 40px 0 으로 덮인다 */}
        <div className="flex flex-col gap-10 py-10">
          <FooterNav color={color} variant="mobile" />
          <FooterCompanyInfo tone={tone} variant="mobile" />
        </div>
        <div className={`flex flex-col gap-1 ${tone.bottom}`}>
          <FooterCopyright variant="mobile" />
          <div className="flex gap-6">
            <div className={`${TERMS_ITEM} ${tone.termsBold}`}>
              <Link href="/terms/privacy" prefetch={false}>
                {t('개인정보처리방침')}
              </Link>
            </div>
            <div className={`${TERMS_ITEM} ${tone.terms}`}>
              <Link href="/terms/term" prefetch={false}>
                {t('이용약관')}
              </Link>
            </div>
            <div className={`${TERMS_ITEM} ${tone.terms}`}>
              <Link href="/terms/cookie" prefetch={false}>
                {t('쿠키정책')}
              </Link>
            </div>
          </div>
        </div>
      </Area>
    </div>
  );
}
